import React, { useState } from 'react';
import { useQuery, useMutation, gql } from '@apollo/client';
import {
  Box, Typography, Button, Card, Table, TableHead, TableRow, TableCell, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, MenuItem, Stack,
  Chip, Alert, LinearProgress, Switch, FormControlLabel,
  Tooltip, Grid, InputAdornment,
} from '@mui/material';
import TableBody from '../components/PagedTableBody'; // pages rows beyond 10
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import { VENUES_QUERY, VENUE_AVAILABILITY_QUERY } from '../graphql/queries';
import { useLov } from '../hooks/useLov';
import ContractedVendorsSection from '../components/ContractedVendorsSection';

const CREATE_VENUE_MUTATION = gql`
  mutation CreateVenue($input: VenueInput!) {
    createVenue(input: $input) {
      id name city venueType capacityMin capacityMax pricePerDay isActive
    }
  }
`;

const UPDATE_VENUE_MUTATION = gql`
  mutation UpdateVenue($id: ID!, $input: VenueInput!) {
    updateVenue(id: $id, input: $input) {
      id name city venueType capacityMin capacityMax pricePerDay isActive
    }
  }
`;

const DELETE_VENUE_MUTATION = gql`
  mutation DeleteVenue($id: ID!) { deleteVenue(id: $id) }
`;

const SET_VENUE_AVAILABILITY_MUTATION = gql`
  mutation SetVenueAvailability($venueId: ID!, $eventDate: String!, $slot: String, $status: String!, $notes: String) {
    setVenueAvailability(venueId: $venueId, eventDate: $eventDate, slot: $slot, status: $status, notes: $notes) {
      id eventDate slot status notes
    }
  }
`;

const emptyVenue = {
  name: '', address: '', city: '', state: '', venueType: '',
  capacityMin: '', capacityMax: '', pricePerDay: '', pricePerHalfDay: '',
  contactName: '', contactPhone: '', contactEmail: '', website: '', notes: '',
};

function AvailabilityDialog({ venue, open, onClose }) {
  const [date, setDate]   = useState('');
  const [slot, setSlot]   = useState('full_day');
  const [status, setStatus] = useState('blocked');
  const [notes, setNotes] = useState('');
  const { options: slotOpts } = useLov('venue_slot');

  const from = new Date().toISOString().slice(0, 10);
  const { data, refetch } = useQuery(VENUE_AVAILABILITY_QUERY, {
    variables: { venueId: venue?.id, fromDate: from },
    skip: !venue?.id,
  });
  const [setAvail] = useMutation(SET_VENUE_AVAILABILITY_MUTATION, {
    onCompleted: () => { setDate(''); setNotes(''); refetch(); },
  });

  const records = data?.venueAvailability || [];
  const blocked = records.filter(r => r.status !== 'available');

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Availability — {venue?.name}</DialogTitle>
      <DialogContent>
        <Typography variant="subtitle2" gutterBottom>Block / Unblock a Date</Typography>
        <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mb: 2 }}>
          <TextField label="Date" type="date" value={date} onChange={e => setDate(e.target.value)}
            InputLabelProps={{ shrink: true }} size="small" sx={{ flex: 1 }} />
          <TextField select label="Slot" value={slot} onChange={e => setSlot(e.target.value)} size="small" sx={{ width: 140 }}>
            {slotOpts.map(s => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
          </TextField>
          <TextField select label="Status" value={status} onChange={e => setStatus(e.target.value)} size="small" sx={{ width: 120 }}>
            <MenuItem value="blocked">Blocked</MenuItem>
            <MenuItem value="available">Available</MenuItem>
          </TextField>
        </Stack>
        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          <TextField label="Notes" value={notes} onChange={e => setNotes(e.target.value)} size="small" sx={{ flex: 1 }} />
          <Button variant="contained" size="small"
            onClick={() => setAvail({ variables: { venueId: venue?.id, eventDate: date, slot, status, notes: notes || null } })}
            disabled={!date}>Save</Button>
        </Stack>

        {blocked.length > 0 && (
          <>
            <Typography variant="subtitle2" gutterBottom>Blocked/Booked Dates (upcoming)</Typography>
            <Table size="small">
              <TableHead><TableRow>
                <TableCell>Date</TableCell><TableCell>Slot</TableCell><TableCell>Status</TableCell><TableCell>Notes</TableCell>
              </TableRow></TableHead>
              <TableBody>
                {blocked.map(r => (
                  <TableRow key={r.id}>
                    <TableCell>{r.eventDate}</TableCell>
                    <TableCell>{r.slot}</TableCell>
                    <TableCell><Chip label={r.status} size="small" color={r.status === 'booked' ? 'error' : 'warning'} /></TableCell>
                    <TableCell>{r.notes || '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </>
        )}
        {blocked.length === 0 && <Alert severity="success">All upcoming dates are available.</Alert>}
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
    </Dialog>
  );
}

export default function AdminVenuesPage() {
  const { options: venueTypeOpts } = useLov('venue_type');
  const { data, loading, error, refetch } = useQuery(VENUES_QUERY, { variables: {}, fetchPolicy: 'cache-and-network' });

  const [form, setForm]       = useState(emptyVenue);
  const [editId, setEditId]   = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [availVenue, setAvailVenue] = useState(null);

  const [createVenue] = useMutation(CREATE_VENUE_MUTATION, { onCompleted: () => { setDialogOpen(false); setForm(emptyVenue); refetch(); } });
  const [updateVenue] = useMutation(UPDATE_VENUE_MUTATION, { onCompleted: () => { setDialogOpen(false); refetch(); } });
  const [deleteVenue] = useMutation(DELETE_VENUE_MUTATION, { onCompleted: refetch });

  const venues = data?.venues || [];

  const openCreate = () => { setForm(emptyVenue); setEditId(null); setDialogOpen(true); };
  const openEdit   = (v) => {
    setForm({
      name: v.name || '', address: v.address || '', city: v.city || '', state: v.state || '',
      venueType: v.venueType || '', capacityMin: v.capacityMin || '', capacityMax: v.capacityMax || '',
      pricePerDay: v.pricePerDay || '', pricePerHalfDay: v.pricePerHalfDay || '',
      contactName: v.contactName || '', contactPhone: v.contactPhone || '',
      contactEmail: v.contactEmail || '', website: v.website || '', notes: v.notes || '',
    });
    setEditId(v.id);
    setDialogOpen(true);
  };

  const buildInput = () => ({
    ...form,
    capacityMin:     form.capacityMin     ? parseInt(form.capacityMin)       : null,
    capacityMax:     form.capacityMax     ? parseInt(form.capacityMax)       : null,
    pricePerDay:     form.pricePerDay     ? parseFloat(form.pricePerDay)     : null,
    pricePerHalfDay: form.pricePerHalfDay ? parseFloat(form.pricePerHalfDay) : null,
  });

  const handleSave = () => {
    if (editId) updateVenue({ variables: { id: editId, input: buildInput() } });
    else        createVenue({ variables: { input: buildInput() } });
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={700}>Manage Venues</Typography>
          <Typography variant="body2" color="text.secondary">Add and manage wedding venues available for booking</Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>Add Venue</Button>
      </Stack>

      <ContractedVendorsSection category="venue" />

      {loading && <LinearProgress />}
      {error   && <Alert severity="error">{error.message}</Alert>}

      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Name</TableCell>
            <TableCell>City / Type</TableCell>
            <TableCell>Capacity</TableCell>
            <TableCell>Price/Day (₹)</TableCell>
            <TableCell>Contact</TableCell>
            <TableCell>Status</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {venues.map(v => (
            <TableRow key={v.id} hover>
              <TableCell><Typography fontWeight={600}>{v.name}</Typography></TableCell>
              <TableCell>
                {v.city}<br />
                <Chip label={v.venueType?.replace('_', ' ') || '—'} size="small" variant="outlined" />
              </TableCell>
              <TableCell>{v.capacityMin || 0}–{v.capacityMax || '∞'}</TableCell>
              <TableCell>{v.pricePerDay ? `₹${Number(v.pricePerDay).toLocaleString('en-IN')}` : '—'}</TableCell>
              <TableCell>{v.contactPhone || '—'}</TableCell>
              <TableCell>
                <Chip label={v.isActive ? 'Active' : 'Inactive'} size="small" color={v.isActive ? 'success' : 'default'} />
              </TableCell>
              <TableCell align="right">
                <Tooltip title="Manage Availability">
                  <IconButton size="small" onClick={() => setAvailVenue(v)}><EventBusyIcon fontSize="small" /></IconButton>
                </Tooltip>
                <IconButton size="small" onClick={() => openEdit(v)}><EditIcon fontSize="small" /></IconButton>
                <IconButton size="small" color="error"
                  onClick={() => window.confirm('Delete this venue?') && deleteVenue({ variables: { id: v.id } })}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
          {venues.length === 0 && !loading && (
            <TableRow><TableCell colSpan={7} align="center">No venues yet. Click "Add Venue" to get started.</TableCell></TableRow>
          )}
        </TableBody>
      </Table>

      {/* Create / Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editId ? 'Edit Venue' : 'Add New Venue'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={8}>
              <TextField label="Venue Name" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} fullWidth required />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField select label="Venue Type" value={form.venueType} onChange={e => setForm(f => ({ ...f, venueType: e.target.value }))} fullWidth>
                <MenuItem value="">— Select —</MenuItem>
                {venueTypeOpts.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField label="Address" value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} fullWidth multiline rows={2} />
            </Grid>
            <Grid item xs={6}><TextField label="City" value={form.city} onChange={e => setForm(f => ({ ...f, city: e.target.value }))} fullWidth /></Grid>
            <Grid item xs={6}><TextField label="State" value={form.state} onChange={e => setForm(f => ({ ...f, state: e.target.value }))} fullWidth /></Grid>
            <Grid item xs={6}><TextField label="Min Capacity" type="number" value={form.capacityMin} onChange={e => setForm(f => ({ ...f, capacityMin: e.target.value }))} fullWidth /></Grid>
            <Grid item xs={6}><TextField label="Max Capacity" type="number" value={form.capacityMax} onChange={e => setForm(f => ({ ...f, capacityMax: e.target.value }))} fullWidth /></Grid>
            <Grid item xs={6}>
              <TextField label="Price Per Day" type="number" value={form.pricePerDay}
                onChange={e => setForm(f => ({ ...f, pricePerDay: e.target.value }))} fullWidth
                InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }} />
            </Grid>
            <Grid item xs={6}>
              <TextField label="Price Per Half Day" type="number" value={form.pricePerHalfDay}
                onChange={e => setForm(f => ({ ...f, pricePerHalfDay: e.target.value }))} fullWidth
                InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }} />
            </Grid>
            <Grid item xs={6}><TextField label="Contact Name" value={form.contactName} onChange={e => setForm(f => ({ ...f, contactName: e.target.value }))} fullWidth /></Grid>
            <Grid item xs={6}><TextField label="Contact Phone" value={form.contactPhone} onChange={e => setForm(f => ({ ...f, contactPhone: e.target.value }))} fullWidth /></Grid>
            <Grid item xs={6}><TextField label="Contact Email" value={form.contactEmail} onChange={e => setForm(f => ({ ...f, contactEmail: e.target.value }))} fullWidth /></Grid>
            <Grid item xs={6}><TextField label="Website" value={form.website} onChange={e => setForm(f => ({ ...f, website: e.target.value }))} fullWidth /></Grid>
            <Grid item xs={12}><TextField label="Notes" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} fullWidth multiline rows={2} /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={!form.name}>{editId ? 'Save Changes' : 'Add Venue'}</Button>
        </DialogActions>
      </Dialog>

      {/* Availability Dialog */}
      <AvailabilityDialog venue={availVenue} open={Boolean(availVenue)} onClose={() => setAvailVenue(null)} />
    </Box>
  );
}
